import React, { useState } from 'react';
import type { DeclineAlertItem } from '../../../../services/optimizationService';

interface DiagnosticsModalProps {
  alert: DeclineAlertItem | null;
  onClose: () => void;
}

export const DiagnosticsModal: React.FC<DiagnosticsModalProps> = ({ alert, onClose }) => {
  const [activeTab, setActiveTab] = useState<'content' | 'technical' | 'competitor'>('content');

  if (!alert || !alert.diagnostics) return null;

  const { content, technical, competitor } = alert.diagnostics;

  const tabs = [
    { id: 'content', label: 'Content Pillar', icon: 'ri-file-text-line', color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/30' },
    { id: 'technical', label: 'Technical Pillar', icon: 'ri-tools-line', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/30' },
    { id: 'competitor', label: 'Competitor Pillar', icon: 'ri-sword-line', color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-900/30' },
  ] as const;

  const currentPillar = activeTab === 'content' ? content : activeTab === 'technical' ? technical : competitor;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/30">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 flex items-center justify-center font-bold text-lg">
              <i className="ri-pulse-line"></i>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  3-Pillar Diagnostics
                </h3>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  alert.severity === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                  alert.severity === 'high' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' :
                  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
                }`}>
                  {alert.severity.toUpperCase()}
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                Keyword: <span className="font-semibold text-gray-900 dark:text-gray-200">"{alert.keyword}"</span>
                {alert.page_url && (
                  <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 truncate max-w-xs inline-block align-bottom">
                    ({alert.page_url})
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Drop Summary Bar */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4 text-xs">
          <div className="flex items-center space-x-4">
            <span className="text-gray-500 dark:text-gray-400">Position Drop:</span>
            <span className="font-bold text-red-600 dark:text-red-400">
              {alert.position_prev != null ? `Pos ${alert.position_prev}` : '—'} → {alert.dropped_out ? 'Dropped (>100)' : alert.position_now != null ? `Pos ${alert.position_now}` : '—'} ({alert.position_delta > 0 ? `-${alert.position_delta}` : alert.position_delta})
            </span>
          </div>
          {alert.click_loss_pct != null && (
            <div className="flex items-center space-x-2">
              <span className="text-gray-500 dark:text-gray-400">Estimated Click Loss:</span>
              <span className="font-bold text-red-600 dark:text-red-400">-{alert.click_loss_pct}%</span>
              {alert.clicks_prev != null && alert.clicks_now != null && (
                <span className="text-gray-400">({alert.clicks_prev} → {alert.clicks_now} clicks)</span>
              )}
            </div>
          )}
        </div>

        {/* Pillar Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6 pt-3 bg-white dark:bg-gray-800">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors -mb-px ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400 font-semibold'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <i className={`${tab.icon} ${activeTab === tab.id ? tab.color : ''}`}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* Findings */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 flex items-center space-x-2">
              <i className="ri-search-eye-line text-blue-500"></i>
              <span>Audit Findings</span>
            </h4>
            {currentPillar.findings && currentPillar.findings.length > 0 ? (
              <div className="space-y-2">
                {currentPillar.findings.map((finding, idx) => (
                  <div
                    key={idx}
                    className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 flex items-start space-x-3 text-sm text-gray-800 dark:text-gray-200"
                  >
                    <i className="ri-checkbox-circle-line text-amber-500 mt-0.5 text-base flex-shrink-0"></i>
                    <span className="leading-relaxed">{finding}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">No specific anomalies flagged in this pillar.</p>
            )}
          </div>

          {/* Recommended Next Step */}
          {currentPillar.next_step && (
            <div className="p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 flex items-center justify-center flex-shrink-0">
                  <i className="ri-direction-line text-lg"></i>
                </div>
                <div>
                  <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                    Recommended Recovery Action
                  </h5>
                  <p className="text-sm text-blue-800 dark:text-blue-300 leading-relaxed">
                    {currentPillar.next_step}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Causes Breakdown */}
          {alert.causes && alert.causes.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Identified Cause Triggers
              </h4>
              <div className="flex flex-wrap gap-2">
                {alert.causes.map((c, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 text-xs font-medium flex items-center space-x-1.5"
                  >
                    <i className="ri-error-warning-line"></i>
                    <span>{c.label}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end bg-gray-50 dark:bg-gray-900/40">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium text-sm transition-colors"
          >
            Close Diagnostics
          </button>
        </div>
      </div>
    </div>
  );
};
